import React, { FC, useCallback, useState } from 'react';

import { Icon, Pressable, Typography } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { StyleSheet, View } from 'react-native';

import Currency from 'components/currency';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getDesignToken } from 'styles/constants';

interface IPrepaidMethodBalance {
  method: IPaymentMethod;
  isDisabled?: boolean;
  isRedesign?: boolean;
  isMasked?: boolean;
}

const formatCardNumber = (cardNumber: string): string => {
  return (cardNumber.match(/.{1,4}/g) || []).join(' ');
};

const MaskDot = ({ color, size }: { color: string; size: number }) => (
  <View style={[styles.maskDot, { backgroundColor: color, width: size, height: size }]} />
);

const MaskGroup = ({ color, size }: { color: string; size: number }) => (
  <View style={styles.maskGroup}>
    {Array.from({ length: 4 }).map((_, index) => (
      <MaskDot key={index} color={color} size={size} />
    ))}
  </View>
);

const MaskedNumber = ({ color, size }: { color: string; size: number }) => (
  <View style={styles.maskNumberContainer}>
    {Array.from({ length: 4 }).map((_, index) => (
      <MaskGroup key={index} color={color} size={size} />
    ))}
  </View>
);

const PrepaidMethodBalance: FC<IPrepaidMethodBalance> = ({
  method,
  isDisabled = false,
  isMasked = false,
}) => {
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );
  const [isNumberVisible, setIsNumberVisible] = useState(false);

  const toggleNumberVisibility = useCallback(() => {
    setIsNumberVisible(prev => !prev);
  }, []);

  const cardNumber = method.prepaid?.cardNumber;
  const textColor = isDisabled ? getDesignToken('text-disabled') : getDesignToken('text-default');

  const lastFour = cardNumber?.slice(-4);
  const optionText = isDigitalGiftCardNumberExposedEnabled
    ? `${t('giftCard')}${lastFour && ` ${t('endsIn').toLowerCase()} ${lastFour}`} -`
    : `${t('giftCard')}`;

  if (!isMasked || !isDigitalGiftCardNumberExposedEnabled) {
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <Typography color={textColor} variant="bodyOne" fontFamily="body" fontSize={15}>
          {optionText}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(method)}
          textProps={{
            style: { margin: 0 },
          }}
        />
      </View>
    );
  }

  const CardNumberRow = cardNumber ? (
    <View style={styles.maskNumberRow}>
      {isNumberVisible ? (
        <Typography fontSize="xs" color={textColor} testID="gift-card-number">
          {formatCardNumber(cardNumber)}
        </Typography>
      ) : (
        <MaskedNumber color={textColor} size={5} />
      )}
      <Pressable
        onPress={toggleNumberVisibility}
        testID="toggle-gift-card-number"
        hitSlop={8}
        accessibilityLabel={isNumberVisible ? 'Hide card number' : 'Show card number'}
        accessibilityRole="button"
      >
        <Icon
          variant={isNumberVisible ? 'visibility' : 'visibilityOff'}
          size="$6"
          color={getDesignToken('icon-default')}
        />
      </Pressable>
    </View>
  ) : null;

  return (
    <View style={{ flexDirection: 'column' }}>
      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
        <Typography
          color={isDisabled ? getDesignToken('text-disabled') : getDesignToken('text-default')}
        >
          {t('giftCard')}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(method)}
          textProps={{
            style: { margin: 0 },
          }}
        />
      </View>
      {CardNumberRow}
    </View>
  );
};

const styles = StyleSheet.create({
  maskDot: {
    width: 5,
    height: 5,
    borderRadius: 5,
  },
  maskGroup: {
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
  },
  maskNumber: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  maskNumberContainer: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  maskNumberRow: {
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
});

export default PrepaidMethodBalance;
